'use client';

import { cn } from '@core/utils';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import React, { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { DesktopHeader } from '@/app/_components/DesktopHeader';
import { FloatingBnbController } from '@/app/_components/FloatingBnbController';
import { GlobalModal } from '@/app/_components/GlobalModal';
import { GlobalNavigation } from '@/app/_components/GlobalNavigation';
import { ToastProvider } from '@/app/_components/ToastProvider';
import { ToastSearchParamsController } from '@/app/_components/ToastSearchParamsController';
import { PageNavigationHost } from '@/components/client/Layout/PageNavigation';
import { SELECTOR } from '@/constants';
import { useGlobalStore } from '@/stores/GlobalStore';
import { findRoute } from '@/utils';

import { HIDE_DESKTOP_HEADER_PATHNAMES } from './constants';
import { ResponsiveDialogContainer } from './ResponsiveDialogContainer';

// SSR 비활성화로 App Router 마운트 이슈 해결
const NaverCtsTracker = dynamic(
  () =>
    import('./NaverCtsTracker').then((mod) => ({
      default: mod.NaverCtsTracker,
    })),
  { ssr: false },
);

interface GlobalLayoutProps {
  children: React.ReactNode;
}

const OptionalGlobalFeatureBoundary = ({
  children,
}: {
  children: React.ReactNode;
}) => <ErrorBoundary fallback={null}>{children}</ErrorBoundary>;

export const GlobalLayout = ({ children }: GlobalLayoutProps) => {
  const pathname = usePathname();
  const currentRoute = findRoute(pathname);
  const hideBottomNav = useGlobalStore((state) => state.hideBottomNav);
  const showDesktopHeader = !HIDE_DESKTOP_HEADER_PATHNAMES.includes(
    pathname ?? '',
  );
  const hasBottomNav = !hideBottomNav && (currentRoute?.hasBottomNav ?? true);

  return (
    <>
      <a
        href={`#${SELECTOR.MAIN}`}
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[9999] focus:rounded-small focus:bg-Gray-black focus:px-16 focus:py-8 focus:font-600 focus:text-Gray-white focus:typo-body4"
      >
        본문 바로가기
      </a>
      {showDesktopHeader && <DesktopHeader />}
      <div
        className={cn(
          'relative flex min-h-dvh max-w-full flex-col overflow-x-clip bg-Gray-white',
          // TODO: 레이아웃 구조 변경 고려. 헤더와 본문 높이 계산을 같은 shell로 합치면 이 예외 분기를 제거할 수 있다.
          showDesktopHeader &&
            'has-[article[data-header-offset-page]]:min-h-[calc(100dvh-4.5rem-1px)] mobile-tablet:has-[article[data-header-offset-page]]:min-h-dvh',
        )}
        id={SELECTOR.MAIN_WRAPPER}
      >
        <div
          className="sticky top-0 z-20 hidden bg-Gray-white mobile-tablet:block"
          id={SELECTOR.PAGE_NAV_WRAPPER}
        >
          <PageNavigationHost />
        </div>
        <OptionalGlobalFeatureBoundary>
          <ResponsiveDialogContainer />
        </OptionalGlobalFeatureBoundary>
        <ToastProvider>
          <ToastSearchParamsController />
          <main
            className={cn(
              'relative flex flex-1 flex-col',
              {
                'mobile-tablet:pb-[calc(70px+var(--safe-area-bottom,0px))]':
                  hasBottomNav,
              },
            )}
            id={SELECTOR.MAIN}
          >
            <Suspense fallback={null}>{children}</Suspense>
            <OptionalGlobalFeatureBoundary>
              <NaverCtsTracker />
            </OptionalGlobalFeatureBoundary>
          </main>
        </ToastProvider>
        <GlobalModal />
        <GlobalNavigation />
        <OptionalGlobalFeatureBoundary>
          <FloatingBnbController />
        </OptionalGlobalFeatureBoundary>
      </div>
    </>
  );
};
